import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { AppService } from "src/app/app.service";
import { HistoricalEstimates } from "src/app/store/models/right-panel-models";
import dayjs from "dayjs";
import { initialState } from "../../store/reducers/right-panel-reducer";
import { Store } from "@ngrx/store";
import { AppState, stateGetter } from "src/app/store/state/app.state";
import { PageParams } from "src/app/store/models/navigation-settings.model";
import {
  VAIconRegistryService,
  close,
  closeCircle,
  plus,
} from "@va/core-library/icons";

declare function gridRefresh(eventTarget, event);
declare function OnDefaultSetting(sectionName);

@Component({
  selector: "app-historical-estimates",
  templateUrl: "./historical-estimates.component.html",
  styleUrls: ["./historical-estimates.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class HistoricalEstimatesComponent implements OnInit {
  datePickerModel: Date[] = [];
  errorArray: string[] = [];
  actionsDisabled = true;
  pageNavParams: PageParams;

  originalModel: HistoricalEstimates = {
    ...initialState.panels.historicalEstimates,
  };
  tempModel: HistoricalEstimates = {
    ...initialState.panels.historicalEstimates,
  };

  constructor(
    private appService: AppService,
    private store: Store<AppState>,
    private iconRegistry: VAIconRegistryService,
  ) {
    this.iconRegistry.registerIcons([close, closeCircle, plus]);
  }

  ngOnInit() {
    window.addEventListener("MEDataSet", (rpObj: any) => {
      if (
        rpObj.detail.section === "Revisions" ||
        rpObj.detail.section === "All"
      ) {
        const data = this.getPanelData(rpObj.detail.data.panels);
        if (data !== undefined) {
          this.setHistoricalEstimatesData(data);
        }
      }
    });

    this.store
      .select(stateGetter("navigationSettings", "pageParams"))
      .subscribe((pageNavParams: PageParams) => {
        this.pageNavParams = pageNavParams;
      });
  }

  setHistoricalEstimatesData = (data) => {
    this.originalModel = {
      changedDate: data.changed_date,
      clearDimension: data.clear_dimension,
      default: data.default,
      effectiveStart: data.effective_start,
      type: data.type,
      revisionDateList: data.revision_date_list,
      values: data.values,
    };

    if (data.revision_date_list.length === 0) {
      this.originalModel.revisionDateList = [null];
      this.originalModel.values = ["Unset"];
    }

    if (
      data.revision_date_list.length === 1 &&
      data.revision_date_list[0] === "PreQ"
    ) {
      this.originalModel.revisionDateList = [null, "PreQ"];
      this.originalModel.values = ["Unset", "PreQ"];
    }

    this.tempModel = JSON.parse(JSON.stringify(this.originalModel));
    this.datePickerModel = [];
    this.actionsDisabled = true;
    this.errorArray = [];

    //push null to datepickerModel for empty datepicker.
    //otherwise push JS date objects
    if (this.tempModel.revisionDateList[0] === null) {
      this.datePickerModel.push(null);
      this.errorArray = [""];
    } else {
      this.tempModel.revisionDateList.forEach((date: string) => {
        if (date !== "PreQ") {
          this.datePickerModel.push(this.getDateObjectFrom(date));
          this.errorArray.push("");
        }
      });
    }
  };

  /**
   *
   * @param current Date object
   * returns true if date should be disabled
   */
  disabledDate = (current: Date): boolean => {
    const today = dayjs();
    const selected = dayjs(current);
    const start =
      this.tempModel.effectiveStart !== ""
        ? dayjs(this.tempModel.effectiveStart, "YYYY-MM-DD")
        : dayjs(0);

    //dayjs objects support comparison like this
    return !(selected < today && selected >= start);
  };

  onDateChange = (dateObj: Date, index: number) => {
    this.tempModel.revisionDateList[index] = this.getFormattedDate(dateObj);

    const validation = this.validateInputDate(dateObj);
    this.errorArray[index] = validation.msg;
    this.handleDisplayType();

    //if date is valid
    //put difference string in tempModel.values
    this.tempModel.values[index] =
      this.appService.getFormattedDateDifference(dateObj);
    this.handleActionBtnsState();
    this.logUserAction("OPTIONS_REVISION_DATE", {
      REVISION_ORDER: index,
      REVISION_VALUE: this.tempModel.values[index],
    });
  };

  onPreqChange = (checked: boolean) => {
    // this.preqChecked = checked;
    if (checked && !this.tempModel.values.includes("PreQ")) {
      this.tempModel.revisionDateList.push("PreQ");
      this.tempModel.values.push("PreQ");
    }
    if (!checked && this.tempModel.values.includes("PreQ")) {
      this.tempModel.revisionDateList.pop();
      this.tempModel.values.pop();
    }

    this.handleDisplayType();
    this.handleActionBtnsState();
    this.logUserAction("OPTIONS_REVISION_PREQ", {
      REVISION_VALUE: checked ? "ON" : "OFF",
    });
  };

  onDisplayTypeChange = () => {
    this.handleActionBtnsState();
    this.logUserAction(
      `OPTIONS_REVISIONS_${this.tempModel.type.toUpperCase()}`,
      {},
    );
  };

  onEffectiveStartClick = (idx) => {
    const effectiveStart = dayjs(
      this.tempModel.effectiveStart,
      "YYYY-MM-DD",
    ).toDate();
    this.datePickerModel[idx] = effectiveStart;
    this.onDateChange(effectiveStart, idx);
  };

  addRevision = () => {
    const datePickerModelLen = this.datePickerModel.length;

    //null value displays empty datepicker
    this.datePickerModel.push(null);
    this.errorArray.push("");

    //revisionDateList and values might contain 'PreQ' at end
    //so insert new null value at index = datePickerModelLen
    //this maintains 'PreQ' at end of the array if present
    this.tempModel.revisionDateList.splice(datePickerModelLen, 0, null);
    this.tempModel.values.splice(datePickerModelLen, 0, null);
  };

  removeRevision = (index: number) => {
    this.logUserAction("OPTIONS_REVISION_DATE_REMOVE", {
      REVISION_ORDER: index,
      REVISION_VALUE: this.tempModel.values[index],
    });

    this.datePickerModel.splice(index, 1);
    this.errorArray.splice(index, 1);
    this.tempModel.revisionDateList.splice(index, 1);
    this.tempModel.values.splice(index, 1);
    this.handleActionBtnsState();
  };

  //force angular to track elemnts in datePickerModel by index
  trackByIdx(index: number): any {
    return index;
  }

  /**
   *
   * @param dateObj Javascript Date object
   * @returns formattedDate string as MM-DD-YYYY
   */
  getFormattedDate = (dateObj: Date) => {
    if (dateObj === null) {
      return null;
    }

    const formattedDate = dayjs(dateObj).format("MM-DD-YYYY");
    return formattedDate;
  };

  /**
   *
   * @param date date as a string in format MM-DD-YY
   * @returns Javascript date object
   */
  getDateObjectFrom = (date: string) => {
    const dateObj = dayjs(date, "MM-DD-YYYY").toDate();
    return dateObj;
  };

  /**
   * toggle to relevant display type among "off", "row" and "column"
   */
  handleDisplayType = () => {
    //if preq is checked or some date picker is populated
    //and current display type is off"
    //then change to "row"
    if (
      (this.tempModel.values.includes("PreQ") ||
        this.datePickerModel.some((date: Date) => date !== null)) &&
      this.tempModel.type === "off"
    ) {
      this.tempModel.type = "row";
    }

    //if preq is not checked and all date pickers are null(empty)
    //and either "row" or "column" is selected as display type
    //then change to "off"
    if (
      !this.tempModel.values.includes("PreQ") &&
      !this.datePickerModel.some((date: Date) => date !== null) &&
      ["row", "column"].includes(this.tempModel.type)
    ) {
      this.tempModel.type = "off";
    }
  };

  handleActionBtnsState = () => {
    this.actionsDisabled = this.appService.compare(
      this.originalModel,
      this.tempModel,
    );
  };

  /**
   * discard all changes made on the tempModel
   * and initialize it to last applied changes (available on originalModel)
   */
  handleCancel = () => {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    this.setHistoricalEstimatesData(window.MEData.panels[panelIdx]);
  };

  /**
   * push changes made on tempModel to store
   */
  handleApply = (evt) => {
    this.datePickerModel.forEach((date: Date, index: number) => {
      const validation = this.validateInputDate(date);
      this.errorArray[index] = validation.msg;
    });

    this.tempModel.clearDimension = true;
    this.tempModel.changedDate = this.getFormattedDate(new Date());
    this.originalModel = JSON.parse(JSON.stringify(this.tempModel));
    this.handleActionBtnsState();
    this.updateMEData(this.tempModel);
    gridRefresh(evt.currentTarget, evt);
  };

  handleClear = (evt: MouseEvent) => {
    //stop collapsing of historical estimates section
    evt.stopImmediatePropagation();
    OnDefaultSetting("Revisions");
  };

  validateInputDate = (date: Date) => {
    if (date === null && this.datePickerModel.length > 1) {
      return { valid: false, msg: "Format date as yyyy-mm-dd" };
    }

    if (this.getFormattedDate(date) === this.getFormattedDate(new Date())) {
      return { valid: false, msg: "Only previous dates can be defined" };
    }

    const count = this.tempModel.revisionDateList.filter(
      (rvdate) => rvdate === this.getFormattedDate(date),
    ).length;

    const currIdx = this.datePickerModel.indexOf(date);
    const firstIdx = this.tempModel.revisionDateList.indexOf(
      this.getFormattedDate(date),
    );
    if (count > 1 && currIdx > firstIdx) {
      return {
        valid: false,
        msg: "You have already selected this date. Each revision must be unique",
      };
    }

    return { valid: true, msg: "" };
  };

  updateMEData(revisionsData: HistoricalEstimates) {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);

    window.MEData.panels[panelIdx] = {
      ...window.MEData.panels[panelIdx],
      changed_date: revisionsData.changedDate,
      clear_dimension: revisionsData.clearDimension,
      default: revisionsData.default,
      effective_start: revisionsData.effectiveStart,
      revision_date_list: [...revisionsData.revisionDateList],
      type: revisionsData.type,
      values: [...revisionsData.values],
    };

    const indicesToRemove = [];
    //clean null values and error values
    for (let i = 0; i < this.errorArray.length; i++) {
      if (
        this.errorArray[i] === "Only previous dates can be defined" ||
        this.errorArray[i] ===
          "You have already selected this date. Each revision must be unique" ||
        this.tempModel.values[i] === null ||
        this.tempModel.values[i] === "Unset"
      ) {
        //remove this index
        indicesToRemove.push(i);
      }
    }

    indicesToRemove.reverse().forEach((idx) => {
      window.MEData.panels[panelIdx].revision_date_list.splice(idx, 1);
      window.MEData.panels[panelIdx].values.splice(idx, 1);
    });

    this.setRowDimensionsForRevisions();
  }

  setRowDimensionsForRevisions() {
    const panelIdx = this.getIndexForPanel(window.MEData.panels);
    const revisionsData = window.MEData.panels[panelIdx];

    const revDates = revisionsData.revision_date_list;
    const revValues = revisionsData.values;

    const sortedDatesAndVals = this.zipSortAndUnzip(revDates, revValues);

    const revisionsDimension = {
      name: "Revisions",
      expansions: sortedDatesAndVals.revisionValuesSorted,
      values: [{ name: "Current", expanded: false }],
    };

    this.removeExistingRevisionDimensions();

    //check what type the user has currently selected
    if (revisionsData.type === "row") {
      window.MEData.row_dimensions.push(revisionsDimension);
    } else if (revisionsData.type === "column") {
      revisionsDimension.expansions.reverse();
      window.MEData.column_dimensions.push(revisionsDimension);
    }
  }

  removeExistingRevisionDimensions() {
    const idxOnRowDim = window.MEData.row_dimensions.findIndex(
      (dimension) => dimension.name === "Revisions",
    );
    const idxOnColDim = window.MEData.column_dimensions.findIndex(
      (dimension) => dimension.name === "Revisions",
    );

    if (idxOnRowDim > -1) {
      window.MEData.row_dimensions.splice(idxOnRowDim, 1);
    }

    if (idxOnColDim > -1) {
      window.MEData.column_dimensions.splice(idxOnColDim, 1);
    }
  }

  zipSortAndUnzip(revisionDateList, revisionValues) {
    //zip together
    let revDatesAndVals = [];
    revisionDateList.forEach((date, i) => {
      revDatesAndVals.push({ date: date, value: revisionValues[i] });
    });

    //sort
    revDatesAndVals = this.appService.basicSort(
      revDatesAndVals,
      ["date"],
      true,
      this.timeMillisComparator,
    );

    //unzip
    const revisionDateListSorted = revDatesAndVals.map(({ date }) => date);
    const revisionValuesSorted = revDatesAndVals.map(({ value }) => value);

    return { revisionDateListSorted, revisionValuesSorted };
  }

  timeMillisComparator(dateStr: string) {
    if (dateStr.toLocaleLowerCase() === "preq") {
      return 8640000000000000; //largest time milliseconds
    }
    const dateObj = dayjs(dateStr, "MM-DD-YYYY").toDate();
    return dateObj.getTime();
  }

  getPanelData(panelsList) {
    return panelsList.find((panel) => panel.name.toLowerCase() === "revisions");
  }

  getIndexForPanel(panelsList) {
    return panelsList.findIndex(
      (panel) => panel.name.toLowerCase() === "revisions",
    );
  }

  logUserAction(feature: string, featureParams) {
    this.appService.logUserAction(
      {},
      this.pageNavParams.ticker,
      this.pageNavParams.cut,
      feature,
      {
        URL: window.location.toString(),
        UTCTIME: new Date().toUTCString(),
        ...featureParams,
      },
      "" || this.pageNavParams.source,
      null,
    );
  }
}
